import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const FirstObservation: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Your First Observation — Sprout Docs</title>
        <meta
          name="description"
          content="Step-by-step guide for teachers creating their first observation in Sprout: choosing a child, picking a CAPS learning area, and publishing to parents."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/docs/getting-started/first-observation" />
      </Helmet>

      <h1>Your First Observation</h1>
      <p className="lead">
        Observations are short, narrative records of what a child did and what
        it tells you about their development. They're the heart of how Sprout
        is used day to day.
      </p>

      <h2>Open the Observations page</h2>
      <p>
        From the sidebar, choose <strong>Observations</strong> and click{' '}
        <strong>Add Observation</strong>. You'll see a form with a child
        selector, a learning-area picker, and a free-text narrative field.
      </p>

      <h2>Pick the child and the learning area</h2>
      <p>
        Start typing the child's name to filter the list. Then choose a CAPS
        Learning Area — for the Foundation Phase that's Home Language, First
        Additional Language, Mathematics, or Life Skills — and the specific
        outcome that the moment maps to. Linking observations to outcomes is
        what makes them show up in CAPS-aligned reports later.
      </p>

      <h2>Write the narrative</h2>
      <p>
        A good observation is short and specific: what the child did, what
        they said, and what it suggests about their progress. Try to describe
        the moment rather than evaluate the child. There's no length
        requirement — two sentences is fine.
      </p>

      <h2>Attach photos (Sprout plan)</h2>
      <p>
        On the Sprout plan you can attach photos or short videos to an
        observation. They're stored alongside the narrative and visible to the
        child's parents once you publish. Photos stay private to the teaching
        team until that point. The free Seedling plan supports text-only
        observations.
      </p>

      <h2>Publish to share with parents</h2>
      <p>
        By default an observation is a draft, visible only to your teaching
        team. Click <strong>Publish</strong> when it's ready for the child's
        guardians to see in the parent portal. You can keep some observations
        unpublished — for example, internal notes about a follow-up — by
        leaving them in draft.
      </p>

      <h2>Recording the same activity for many children</h2>
      <p>
        For group activities, the <strong>Bulk Observations</strong> page lets
        you write one narrative and apply it across a list of children, then
        edit each one individually if you want to add a child-specific note.
        This saves a lot of time during a busy week.
      </p>

      <p>
        Next: see how this fits into the curriculum view in{' '}
        <Link to="/docs/curriculum/caps-alignment">CAPS Alignment</Link>.
      </p>
    </>
  );
};

export default FirstObservation;
